import { z } from "zod";

import { AssignmentRowSchema } from "@/schemas/business";
import type {
  AssignmentRow,
  BusinessShiftListResponse,
} from "@/schemas/business";
import { OfferListResponseSchema } from "@/schemas/offer";

import { apiRequest } from "./apiUtils";

const BusinessShiftListEnvelopeSchema = z.object({
  message: z.string().nullable(),
  success: z.boolean().default(false),
  assignments: z.array(z.unknown()),
  total: z.number(),
});

function parseAssignmentsResiliently(data: unknown): BusinessShiftListResponse {
  const envelope = BusinessShiftListEnvelopeSchema.parse(data);
  const assignments: Array<AssignmentRow> = [];
  envelope.assignments.forEach((raw, i) => {
    const result = AssignmentRowSchema.safeParse(raw);
    if (result.success) {
      assignments.push(result.data);
    } else {
      console.warn(
        `[businessShiftApi] dropping malformed assignment at index ${i}`,
        result.error.issues,
      );
    }
  });
  return {
    message: envelope.message,
    success: envelope.success,
    assignments,
    total: envelope.total,
  };
}

export async function listBusinessShifts({
  pageSize = 100,
  startDate = undefined,
  endDate = undefined,
}: {
  pageSize?: number;
  startDate?: string;
  endDate?: string;
} = {}) {
  const request: any = {
    page: 0,
    page_size: pageSize,
    sort_order: "desc",
  };

  if (startDate) {
    request.start_date = startDate;
  }
  if (endDate) {
    request.end_date = endDate;
  }
  const data = await apiRequest<unknown>("business/shift/list-assignment", {
    method: "POST",
    body: JSON.stringify(request),
  });
  const parsed = parseAssignmentsResiliently(data);
  parsed.assignments = parsed.assignments.sort(
    (a, b) =>
      new Date(a.start_date_time).getTime() -
      new Date(b.start_date_time).getTime(),
  );
  return parsed;
}

export async function listBusinessOffers({
  pageSize = 20,
  startDate = undefined,
  endDate = undefined,
}: {
  pageSize?: number;
  startDate?: string;
  endDate?: string;
} = {}) {
  const request: any = {
    page: 0,
    page_size: pageSize,
  };

  if (startDate) {
    request.start_date = startDate;
  }
  if (endDate) {
    request.end_date = endDate;
  }
  const data = await apiRequest<unknown>("business/shift/list-offer", {
    method: "POST",
    body: JSON.stringify(request),
  });
  return OfferListResponseSchema.parse(data);
}

export async function getWorkerShiftHistory(
  workerId: string,
  { pageSize = 200 }: { pageSize?: number } = {},
): Promise<BusinessShiftListResponse> {
  const data = await apiRequest<unknown>(
    `business/shift/worker/${encodeURIComponent(workerId)}/history`,
    {
      method: "POST",
      body: JSON.stringify({
        page: 0,
        page_size: pageSize,
        sort_order: "desc",
      }),
    },
  );
  return parseAssignmentsResiliently(data);
}

export async function addUpdateOffer({
  userId,
  shiftId,
  offerStatus,
}: {
  userId: string;
  shiftId: number;
  offerStatus: string;
}): Promise<{ success: boolean; message?: string }> {
  const data = await apiRequest<unknown>("business/shift/add-update-offer", {
    method: "POST",
    body: JSON.stringify({
      user_id: userId,
      shift_id: shiftId,
      offer_status: offerStatus,
    }),
  });
  return { ...(data as { success: boolean; message?: string }) };
}
